import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';

import { TPropertyStatus } from '@shared/models/properties';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { selectFiltersCurrent } from '@state/filters/filters.selectors';
import {
  setUnsavedStatusFiltersAction,
  setUnsavedIsVerifiedFiltersAction,
  resetFiltersAction,
} from '@state/filters/filters.actions';

const DEFAULT_STATUSES: TPropertyStatus[] = ['OPEN'];

@UntilDestroy()
@Component({
  selector: 'app-filter-v2-status',
  templateUrl: './filter-v2-status.component.html',
  styleUrls: [
    './filter-v2-status.component.scss',
    '../filters-v2.component.scss',
  ],
})
export class FilterV2StatusComponent implements OnInit {
  @Input() mobile = false;
  @Output() applyFilters = new EventEmitter();
  @ViewChild('trigger') menuTrigger!: MatMenuTrigger;

  protected isOpen = false;
  protected checkedCount = 0;
  protected allSelected = false;

  // Only if true the "Display 3rd Party" checkbox is unchecked
  protected isVerified: boolean | undefined = undefined;

  protected statuses: TPropertyStatus[] = ['OPEN', 'PENDING', 'SOLD'];
  public checkedStatuses: { [key: string]: boolean } = this.statuses.reduce(
    (acc, status) => ({
      ...acc,
      [status]: false,
    }),
    {}
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions
  ) {}

  ngOnInit(): void {
    // for loading the first time
    this.store
      .select(selectFiltersCurrent)
      .pipe(take(1))
      .subscribe((filters) => {
        if (filters?.status && filters.status.in) {
          if (filters.status.in.length === this.statuses.length) {
            this.allSelected = true;
          }

          if (filters.isVerified?.eq !== undefined) {
            this.isVerified = filters.isVerified.eq;
          } else {
            this.isVerified = undefined;
          }

          this.checkedStatuses = this.statuses.reduce(
            (acc, status) => ({
              ...acc,
              [status]: filters?.status?.in?.includes(status),
            }),
            {}
          );
          this.onChangeStatuses();
        }
      });

    // for clearing the filters
    // this.store
    //   .select(selectFiltersCurrent)
    //   .pipe(untilDestroyed(this))
    //   .subscribe((filters) => {
    //     if (
    //       !filters?.status?.in &&
    //       (this.checkedCount > 0 || !this.isVerified)
    //     ) {
    //       this.checkedCount = 0;
    //       this.allSelected = false;
    //       this.isVerified = undefined;
    //       this.checkedStatuses = this.statuses.reduce(
    //         (acc, status) => ({
    //           ...acc,
    //           [status]: DEFAULT_STATUSES.includes(status),
    //         }),
    //         {}
    //       );
    //       this.wasManuallyReset = false; // reset flag
    //     }
    //   });

    this.actions$
      .pipe(ofType(resetFiltersAction), untilDestroyed(this))
      .subscribe(() => {
        this.resetStatuses();
        this.resetIsVerified();
      });
  }

  onChangeStatuses() {
    const statusesLength = Object.values(this.checkedStatuses).filter(
      (status) => status
    ).length;
    this.checkedCount = statusesLength;
    if (statusesLength === this.statuses.length) {
      this.allSelected = true;
    } else {
      this.allSelected = false;
    }
    if (statusesLength) {
      this.store.dispatch(
        setUnsavedStatusFiltersAction({
          in: [
            ...Object.keys(this.checkedStatuses).filter(
              (key: string) => this.checkedStatuses[key]
            ),
          ] as TPropertyStatus[],
        })
      );
    } else {
      this.store.dispatch(setUnsavedStatusFiltersAction({}));
    }
    if (!this.mobile) this.applyFilters.emit();
  }

  toggleAllSelection() {
    this.allSelected = !this.allSelected;
    this.checkedStatuses = this.statuses.reduce(
      (acc, status) => ({
        ...acc,
        [status]: this.allSelected,
      }),
      {}
    );
    this.onChangeStatuses();
  }

  resetStatuses() {
    this.checkedStatuses = this.statuses.reduce(
      (acc, status) => ({
        ...acc,
        [status]: DEFAULT_STATUSES.includes(status),
      }),
      {}
    );

    this.resetIsVerified();
    this.onChangeStatuses();
  }

  closeMenu() {
    this.menuTrigger.closeMenu();
  }

  loadInitialValue() {
    this.store
      .select(selectFiltersCurrent)
      .pipe(take(1))
      .subscribe((filters) => {
        if (filters?.status && filters.status.in) {
          if (filters.status.in.length === this.statuses.length) {
            this.allSelected = true;
          }
          this.checkedStatuses = this.statuses.reduce(
            (acc, status) => ({
              ...acc,
              [status]: filters?.status?.in?.includes(status),
            }),
            {}
          );
          this.onChangeStatuses();
        }
      });
  }

  toggleIsVerified() {
    this.isVerified = !this.isVerified;

    this.store.dispatch(
      setUnsavedIsVerifiedFiltersAction({ eq: this.isVerified })
    );

    if (!this.mobile) this.applyFilters.emit();
  }

  resetIsVerified() {
    this.isVerified = undefined;
    this.store.dispatch(setUnsavedIsVerifiedFiltersAction({}));
    if (!this.mobile) this.applyFilters.emit();
  }

  trackByStatus(index: number, status: string): string {
    return status;
  }
}
